import moment from 'moment';
import { API_DATE_FORMAT, PTO_TYPES } from '@ubeya/shared/constants';
import exportExcel from './exportExcel';

const DEPRECATED_TEXT = ', Invalid date';

const FIELD_SLUGS = {
  // Fields that their slug is different than the localization key
  PHONE_SECONDARY: 'phoneSecondary',
  EMPLOYMENT_TYPE_ID: 'employmentTypeId',
  DEPARTMENT_ID: 'departmentId',
  IS_PENDING: 'isPending',
  IS_DAILY: 'isDaily',
  IS_SHIFT: 'isShift'
};

// Fields that their slug is different than the localization key
const FIELD_NAMES_BY_SLUG = {
  [FIELD_SLUGS.PHONE_SECONDARY]: 'secondaryPhone',
  [FIELD_SLUGS.EMPLOYMENT_TYPE_ID]: 'employmentType',
  [FIELD_SLUGS.DEPARTMENT_ID]: 'department',
  [FIELD_SLUGS.IS_PENDING]: 'pendingApproval',
  [FIELD_SLUGS.IS_DAILY]: 'daily',
  [FIELD_SLUGS.IS_SHIFT]: 'shift'
};

const PTO_LABELS = {
  [PTO_TYPES.Holiday]: 'vacation',
  [PTO_TYPES.SickDay]: 'sick'
};

const getProfileFieldName = (t, profileFieldSlug) => {
  if (profileFieldSlug?.includes('payrollFields')) {
    return profileFieldSlug
      .split('.')
      .map((slug) => t(slug))
      .join(' - ');
  }

  if (Object.keys(FIELD_NAMES_BY_SLUG).includes(profileFieldSlug)) return t(FIELD_NAMES_BY_SLUG[profileFieldSlug]);

  return t(profileFieldSlug);
};

const formatShiftDetails = ({ shift, shortDateMonthFormat, timeFormat }) => {
  const { shiftDate, shiftStartTime, shiftEndTime, shiftPosition, shiftLocation } = shift;

  // dana 13:00-14:00, area
  const details = [];

  if (shiftDate) details.push(moment(shiftDate, API_DATE_FORMAT).format(shortDateMonthFormat));
  if (shiftPosition) details.push(shiftPosition);
  if (shiftStartTime) details.push(moment.utc(shiftStartTime).format(timeFormat));
  if (shiftEndTime) details.push(`-${moment.utc(shiftEndTime).format(timeFormat)}`);
  if (shiftLocation) details.push(`, ${shiftLocation}`);

  return details.join('');
};

export const generateAuditLogBody = ({
  t,
  type,
  data,
  timeFormat = 'HH:mm',
  shortDateMonthFormat = 'DD/MM',
  getProfileFieldValue
}) => {
  const reporterTitle = data?.data?.reportedType === 'admin' ? t('Administrator') : t('User');

  const {
    reportedType,
    reporterName,
    startTime: startTimeRaw,
    endTime: endTimeRaw,
    checkInTime: checkInTimeRaw,
    checkOutTime: checkOutTimeRaw,
    employeeName,
    shiftName,
    shiftDate,
    shiftPosition,
    shiftLocation,
    date,
    hourlyRate,
    adminName,
    jobDate,
    positionName,
    projectName,
    projectDate,
    projectLocation,
    captainName,
    positionSlug,
    value,
    message,
    slug,
    srcProjectDate,
    deactivateReason,
    isFile,
    isPhoto,
    fileChange,
    fieldType,
    fieldName,
    prevValue,
    newValue,
    prevShift,
    newShift,
    branchName,
    isPaused,
    isUpdatedAudience,
    contractName,
    contractType,
    amount,
    ptoType,
    startDate,
    endDate,
    bulkRequestsAmount
  } = data.data;

  const startTime = startTimeRaw && moment.utc(startTimeRaw).format(timeFormat);
  const endTime = endTimeRaw && moment.utc(endTimeRaw).format(timeFormat);
  const checkInTime = checkInTimeRaw && moment.utc(checkInTimeRaw).format(timeFormat);
  const checkOutTime = checkOutTimeRaw && moment.utc(checkOutTimeRaw).format(timeFormat);

  let text;
  let values = {};

  switch (type) {
    case 'UpdatePayrollField': {
      const slugTrans = t(slug);

      text = t('updatePayrollFieldAuditLog', {
        adminName,
        slugTrans,
        value,
        employeeName: employeeName || reporterName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ResetTimesheetTime': {
      text = t(shiftDate ? 'timesheetResetTimeCreateAuditLog' : 'timesheetJobResetTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetStartTime': {
      text = t(
        reportedType === 'admin'
          ? shiftDate
            ? 'timesheetStartTimeCreateAuditLog'
            : 'timesheetJobStartTimeCreateAuditLog'
          : 'timesheetUserStartTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
          startTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetEndTime': {
      text = t(
        reportedType === 'admin'
          ? shiftDate
            ? 'timesheetEndTimeCreateAuditLog'
            : 'timesheetJobEndTimeCreateAuditLog'
          : 'timesheetUserEndTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
          endTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetCheckInTime': {
      text = t(
        reportedType === 'admin'
          ? shiftDate
            ? 'timesheetCheckInTimeCreateAuditLog'
            : 'timesheetJobCheckInTimeCreateAuditLog'
          : 'timesheetUserCheckInTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
          checkInTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'CreateTimesheetCheckOutTime': {
      text = t(
        reportedType === 'admin'
          ? shiftDate
            ? 'timesheetCheckOutTimeCreateAuditLog'
            : 'timesheetJobCheckOutTimeCreateAuditLog'
          : 'timesheetUserCheckOutTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
          checkOutTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetStartTime': {
      text = t(shiftDate ? 'clientTimesheetStartTimeCreateAuditLog' : 'clientTimesheetJobStartTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        startTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetEndTime': {
      text = t(shiftDate ? 'clientTimesheetEndTimeCreateAuditLog' : 'clientTimesheetJobEndTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        endTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetCheckInTime': {
      text = t(shiftDate ? 'clientTimesheetCheckInTimeCreateAuditLog' : 'clientTimesheetJobCheckInTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        checkInTime
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'ClientCreateTimesheetCheckOutTime': {
      text = t(
        shiftDate ? 'clientTimesheetCheckOutTimeCreateAuditLog' : 'clientTimesheetJobCheckOutTimeCreateAuditLog',
        {
          adminName: reporterName,
          reporterTitle,
          employeeName: employeeName || reporterName,
          shiftName,
          date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat),
          shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
          checkOutTime
        }
      ).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'AutoFillTimesheet': {
      text = t('timesheetAutoFillTimeCreateAuditLog', {
        adminName: reporterName,
        reporterTitle,
        employeeName: employeeName || reporterName,
        shiftName,
        startTime,
        endTime,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');

      break;
    }

    case 'AutoClockedOut': {
      text = t('shiftAutoClockedOutCreateAuditLog', {
        employeeName: employeeName,
        shiftName,
        endTime,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      }).replace(DEPRECATED_TEXT, '');
      break;
    }

    case 'UpdateJobPayroll': {
      values = {
        hourlyRate,
        employeeName,
        adminName,
        jobDate: moment(new Date(jobDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'updateJobPayrollAuditLog';

      break;
    }
    case 'UpdateEmployeeSlotPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'updateEmployeeSlotPayrollAuditLog';

      break;
    }

    case 'UpdateShiftPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };

      text = 'updateShiftPayrollAuditLog';

      break;
    }

    case 'UpdateEmployeePayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        jobDate,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'updateEmployeePayrollAuditLog';

      break;
    }

    case 'UpdateEmployeePositionPayroll': {
      values = {
        hourlyRate,
        employeeName,
        shiftName,
        adminName,
        jobDate,
        positionName: t(positionName),
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'updateEmployeePositionPayrollAuditLog';
      break;
    }

    case 'EmployeeBroadcastForShift': {
      values = {
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName
      };
      text = 'employeeBroadcastForShiftAuditLog';

      break;
    }

    case 'ApprovedWorkingHours': {
      values = {
        adminName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        employeeName,
        startTime,
        endTime,
        reporterName
      };
      text = 'approvedWorkingHoursAuditLog';

      break;
    }

    case 'UnApprovedWorkingHours': {
      values = {
        adminName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        employeeName,
        startTime,
        endTime,
        reporterName
      };
      text = 'UnApprovedWorkingHoursAuditLog';

      break;
    }

    case 'EmployeeBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName
      };
      text = 'employeeBookedForShiftAuditLog';

      break;
    }

    case 'EmployeeUnBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        shiftPosition: shiftPosition ? ` - ${shiftPosition}` : '',
        employeeName,
        message: message ? `. ${message}` : ''
      };

      text = 'employeeUnBookedForShiftAuditLog';
      break;
    }

    case 'EmployeeRequestReleaseFromShift': {
      values = {
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        employeeName
      };

      text = 'EmployeeRequestReleaseFromShiftAuditLog';
      break;
    }

    case 'EmployeeAutoBookedForShift': {
      values = {
        adminName,
        shiftName,
        shiftDate: moment(new Date(shiftDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        employeeName
      };
      text = 'employeeAutoBookedForShiftAuditLog';

      break;
    }

    case 'EmployeeLeftCompany': {
      values = { employeeName };
      text = 'employeeLeftCompanyAuditLog';
      break;
    }

    case 'EmployeeCreatedByAPI': {
      values = { employeeName };
      text = 'employeeCreatedByAPIAuditLog';
      break;
    }

    case 'EmployeeDeletedByAPI': {
      values = { employeeName };
      text = 'employeeDeletedByAPIAuditLog';
      break;
    }

    case 'EmployeeSMSInvited': {
      values = { employeeName, adminName };
      text = 'employeeSMSInvitedAuditLog';
      break;
    }

    case 'EmployeeChangeShiftAutoLogUseCase': {
      if (prevShift && newShift) {
        values = {
          adminName,
          prevShift: formatShiftDetails({
            shift: prevShift,
            shortDateMonthFormat,
            timeFormat
          }),
          newShift: formatShiftDetails({ shift: newShift, shortDateMonthFormat, timeFormat }),
          employeeName
        };
        text = 'employeeChangedShiftAuditLogNew';
      } else {
        values = {
          adminName,
          shiftDate,
          employeeName
        };
        text = 'employeeChangedShiftAuditLogOld';
      }

      break;
    }

    case 'ShiftCheckAvailabilityAutoLog': {
      const formattedDate = shiftDate && moment.utc(shiftDate).format(shortDateMonthFormat);
      const shiftTime =
        startTime && endTime ? `${formattedDate} ${startTime} - ${endTime}` : `${formattedDate} ${startTime}`;
      const position = shiftPosition ? `${shiftPosition}, ` : '';
      const area = shiftLocation === projectLocation ? '' : `, ${shiftLocation}`;
      const eventName = projectName ? `${projectName}, ` : '';

      values = {
        adminName,
        position,
        shiftTime,
        area,
        projectName: eventName,
        projectLocation,
        branchName
      };
      if (isPaused !== undefined) {
        text = isPaused ? 'shiftCheckAvailabilityPausedAuditLog' : 'shiftCheckAvailabilityResumedAuditLog';
        break;
      }
      if (isUpdatedAudience) {
        text = 'shiftCheckAvailabilityUpdatedAudienceAuditLog';
        break;
      }
      text = 'shiftCheckAvailabilityCreatedAuditLog';
      break;
    }

    case 'AdminUploadedEmployees': {
      values = { adminName };
      text = 'adminUploadedEmployeesAuditLog';
      break;
    }

    case 'AdminUploadedEmployeesJobs': {
      values = { adminName };
      text = 'adminUploadedEmployeesJobsAuditLog';
      break;
    }

    case 'EmployeeDeleted': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeDeleteAuditLog';
      break;
    }

    case 'EmployeeRestored': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeRestoredAuditLog';
      break;
    }

    case 'EmployeeCreated': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeCreateAuditLog';
      break;
    }
    case 'EmployeeUpdated': {
      values = {
        adminName,
        employeeName,
        fieldName: getProfileFieldName(t, fieldName),
        fileChange,
        document: isPhoto ? 'photo' : 'file'
      };

      const isDocument = isFile || isPhoto;

      if (!isDocument) {
        values.prevValue = getProfileFieldValue?.({
          profileFieldName: fieldName,
          profileFieldType: fieldType,
          profileFieldValue: prevValue
        });

        values.newValue = getProfileFieldValue?.({
          profileFieldName: fieldName,
          profileFieldType: fieldType,
          profileFieldValue: newValue
        });
      }

      text = isDocument
        ? 'employeeUploadDocumentAuditLog'
        : fieldName
        ? 'employeeUpdateFieldAuditLog'
        : 'employeeUpdateAuditLog';
      break;
    }

    case 'EmployeeActivated': {
      values = {
        adminName,
        employeeName
      };
      text = 'employeeActivatedAuditLog';
      break;
    }
    case 'EmployeeDeactivated': {
      values = {
        adminName,
        employeeName,
        deactivateReason
      };
      text = deactivateReason ? 'employeeDeactivatedWithReasonAuditLog' : 'employeeDeactivatedAuditLog';
      break;
    }

    case 'ProjectCreated': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat),
        srcProjectDate: srcProjectDate
          ? moment(new Date(srcProjectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
          : undefined
      };
      text = srcProjectDate ? 'projectCreateViaCopyAuditLog' : 'projectCreateAuditLog';
      break;
    }

    case 'ProjectDeleted': {
      const { projectEndDate } = data.data;
      const dateFormatted = moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat);
      const endDateFormatted = moment(new Date(projectEndDate), API_DATE_FORMAT).format(shortDateMonthFormat);
      values = {
        adminName,
        projectName,
        projectLocation,
        projectDate: dateFormatted !== endDateFormatted ? `${dateFormatted} - ${endDateFormatted}` : dateFormatted
      };
      text = 'projectDeletedAuditLog';
      break;
    }

    case 'ProjectCaptainTimesheetStartTimeUpdated': {
      values = {
        captainName,
        employeeName,
        projectName,
        startTime,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectCaptainTimesheetStartTimeUpdated';
      break;
    }
    case 'ProjectCaptainTimesheetEndTimeUpdated': {
      values = {
        captainName,
        endTime,
        employeeName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectCaptainTimesheetEndTimeUpdated';

      break;
    }
    case 'ProjectCaptainTimesheetCheckInTimeUpdated': {
      values = {
        captainName,
        endTime,
        employeeName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectCaptainTimesheetCheckInTimeUpdated';

      break;
    }
    case 'ProjectCaptainTimesheetCheckOutTimeUpdated': {
      values = {
        captainName,
        endTime,
        employeeName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectCaptainTimesheetCheckOutTimeUpdated';

      break;
    }
    case 'ProjectInvoiceIssue': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectInvoiceIssue';
      break;
    }
    case 'ProjectArchived': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectArchived';

      break;
    }

    case 'ProjectUnArchived': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectUnArchived';

      break;
    }

    case 'ProjectUpdated': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'projectUpdateAuditLog';

      break;
    }

    case 'ShiftUpdated': {
      values = {
        adminName,
        shiftName: positionSlug ? t(positionSlug) : '',
        ...(startTime && { startTime }),
        ...(endTime && { endTime })
      };
      text =
        startTime && endTime
          ? 'shiftUpdateAuditLogTime'
          : startTime
          ? 'shiftUpdateAuditLogStartTime'
          : endTime
          ? 'shiftUpdateAuditLogEndTime'
          : '';

      break;
    }

    case 'ApplicationCreated': {
      values = {
        adminName,
        projectName,
        projectDate: moment(new Date(projectDate), API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'applicationCreateAuditLog';

      break;
    }

    case 'RequestDeclined': {
      values = {
        adminName,
        employeeName
      };
      text = 'requestDeclinedAuditLog';

      break;
    }

    case 'RequestApproved': {
      values = {
        adminName,
        employeeName
      };
      text = 'requestApprovedAuditLog';
      break;
    }

    case 'RequestsApproved': {
      values = {
        adminName,
        amount: bulkRequestsAmount
      };
      text = bulkRequestsAmount === 1 ? 'recentActionBulkApprovedRequest' : 'recentActionBulkApprovedRequests';
      break;
    }

    case 'RequestsDeclined': {
      values = {
        adminName,
        amount: bulkRequestsAmount
      };
      text = bulkRequestsAmount === 1 ? 'recentActionBulkDeclinedRequest' : 'recentActionBulkDeclinedRequests';
      break;
    }

    case 'IsHostAccountActivated': {
      values = {
        adminName
      };
      text = 'IsHostAccountActivated';
      break;
    }

    case 'AccountContractCreated': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_createdContract_auditLog';
      break;
    }

    case 'AccountContractUpdated': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_changeContract_auditLog';
      break;
    }
    case 'AccountContractDeleted': {
      values = {
        adminName,
        contractName,
        contractType
      };
      text = 'ContractTypes_deleteContract_auditLog';
      break;
    }
    case 'EmployeeAssignedContract': {
      values = {
        adminName,
        employeeName,
        contractName
      };
      text = 'ContractTypes_assignEmployee_auditLog';
      break;
    }
    case 'EmployeeContractAdjusted': {
      values = {
        adminName,
        employeeName,
        contractName
      };
      text = 'ContractTypes_adjustEmployeeContract_auditLog';
      break;
    }
    case 'EmployeePtoAllowanceAdjusted': {
      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType])
      };
      text = 'ContractTypes_adjustAllowance_auditLog';
      break;
    }
    case 'AdminAddedEmployeePto': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType]),
        amount,
        dates
      };
      if (amount > 1) {
        text =
          ptoType === PTO_TYPES.Holiday
            ? 'ContractTypes_adminAddedPTOsHoliday_auditLog'
            : 'ContractTypes_adminAddedPTOsSickday_auditLog';
      } else {
        text = 'ContractTypes_adminAddedPTO_auditLog';
      }
      break;
    }
    case 'AdminDeletedEmployeePto': {
      values = {
        adminName,
        employeeName,
        ptoType: t(PTO_LABELS[ptoType]),
        date: moment(date, API_DATE_FORMAT).format(shortDateMonthFormat)
      };
      text = 'ContractTypes_adminDeletedPTO_auditLog';
      break;
    }
    case 'AdminApprovedHolidayRequest': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        dates
      };
      text = 'ContractTypes_approveTimeOffRequest_auditLog';
      break;
    }
    case 'AdminApprovedSickLeaveRequest': {
      const dates =
        amount > 1
          ? `${moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat)} - ${moment(
              endDate,
              API_DATE_FORMAT
            ).format(shortDateMonthFormat)}`
          : moment(startDate, API_DATE_FORMAT).format(shortDateMonthFormat);

      values = {
        adminName,
        employeeName,
        dates
      };
      text = 'ContractTypes_approveSickDayRequest_auditLog';
      break;
    }

    default:
      break;
  }

  return { text, values };
};

export const exportFormattedAuditLogs = ({
  t,
  auditLogs = [],
  dateFormat = 'DD/MM/YYYY',
  timeFormat = 'HH:mm',
  shortDateMonthFormat = 'DD/MM',
  title = 'auditLogs',
  getProfileFieldValue
}) => {
  const formattedData = auditLogs.reduce((prev, { type, data: payload, createdAt, ...rest }) => {
    const { text, values } = generateAuditLogBody({
      t,
      type,
      data: { type, data: JSON.parse(payload), createdAt, ...rest },
      timeFormat,
      shortDateMonthFormat,
      getProfileFieldValue
    });

    const details = (Object?.keys(values)?.length === 0 ? t(text) : t(text, values) || '')
      .replace(/<brs*[/]?>/gi, '\n')
      .replace(/<[^>]*>/gi, '');

    return [
      ...prev,
      {
        [t('createdAt')]: moment.utc(createdAt).local().format(`${dateFormat} ${timeFormat}`),
        [t('details')]: details
      }
    ];
  }, []);

  exportExcel({ baseData: formattedData, name: t(title) });
};
